import { useApi } from '@backstage/core-plugin-api';
import type {
  ActivityTerminalResult,
  UsageRecoveryAuthorizationResult,
} from '@internal/spassu-code-contract';
import { MutableRefObject, useCallback, useState } from 'react';
import { controlPlaneApiRef, ControlPlaneApiError } from '../api';

export type ManagementOperation =
  | 'idle'
  | 'finalizing'
  | 'cancelling'
  | 'recovering-usage';

export interface ProjectionActionReader {
  setError(next: string | null): void;
  load(force?: boolean): Promise<void>;
  mounted: MutableRefObject<boolean>;
}

function messageFor(error: unknown): string {
  if (error instanceof ControlPlaneApiError) return error.message;
  return error instanceof Error ? error.message : 'A gestão da conexão falhou.';
}

/** Distinct per attempt so a retry is never collapsed into the previous one. */
function idempotencyKey(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function outcomeAnnouncement(result: ActivityTerminalResult): string {
  return result.outcome === 'completed'
    ? 'Atividade concluída.'
    : 'Atividade cancelada.';
}

type TerminalRunner = (
  kind: ManagementOperation,
  run: () => Promise<ActivityTerminalResult>,
  pending: string,
) => Promise<ActivityTerminalResult | null>;

function useTerminalRunner(
  reader: ProjectionActionReader,
  setOperation: (next: ManagementOperation) => void,
  setAnnouncement: (next: string) => void,
): TerminalRunner {
  const { load, setError, mounted } = reader;
  return useCallback(
    async (kind, run, pending) => {
      setOperation(kind);
      setAnnouncement(pending);
      setError(null);
      try {
        const result = await run();
        if (mounted.current) setAnnouncement(outcomeAnnouncement(result));
        await load(true);
        return result;
      } catch (caught) {
        if (mounted.current) {
          setError(messageFor(caught));
          setAnnouncement('');
        }
        return null;
      } finally {
        if (mounted.current) setOperation('idle');
      }
    },
    [load, setError, mounted, setOperation, setAnnouncement],
  );
}

export function useTerminalActions(
  activityId: string,
  reader: ProjectionActionReader,
) {
  const api = useApi(controlPlaneApiRef);
  const [operation, setOperation] = useState<ManagementOperation>('idle');
  const [announcement, setAnnouncement] = useState('');
  const runTerminal = useTerminalRunner(reader, setOperation, setAnnouncement);

  return {
    operation,
    announcement,
    setOperation,
    setAnnouncement,
    finalize: useCallback(
      () =>
        runTerminal(
          'finalizing',
          () => api.finalizeActivity(activityId, idempotencyKey('finalize')),
          'Concluindo a atividade.',
        ),
      [api, activityId, runTerminal],
    ),
    cancel: useCallback(
      (reason: string) =>
        runTerminal(
          'cancelling',
          () =>
            api.cancelActivity(activityId, reason, idempotencyKey('cancel')),
          'Cancelando a atividade.',
        ),
      [api, activityId, runTerminal],
    ),
  };
}

export function useUsageRecoveryAction(
  activityId: string,
  reader: ProjectionActionReader,
  setOperation: (next: ManagementOperation) => void,
  setAnnouncement: (next: string) => void,
) {
  const api = useApi(controlPlaneApiRef);
  const [authorization, setAuthorization] =
    useState<UsageRecoveryAuthorizationResult | null>(null);
  const { load, setError, mounted } = reader;
  const authorize = useCallback(async () => {
    setOperation('recovering-usage');
    setAuthorization(null);
    setError(null);
    setAnnouncement('Autorizando recuperação de tokens.');
    try {
      const result = await api.authorizeUsageRecovery(activityId);
      if (mounted.current) {
        setAuthorization(result);
        setAnnouncement(
          'Recuperação autorizada. Use o comando e o código no clone correto.',
        );
      }
      await load(true);
      return result;
    } catch (caught) {
      if (mounted.current) {
        setError(messageFor(caught));
        setAnnouncement('A recuperação de tokens não foi autorizada.');
      }
      return null;
    } finally {
      if (mounted.current) setOperation('idle');
    }
  }, [activityId, api, load, mounted, setAnnouncement, setError, setOperation]);
  return { authorization, authorize };
}
